import { useMemo, useState } from "react";
import { Link, useParams } from "wouter";
import { CarLogo } from "../components/common/CarLogo.js";
import { useModals } from "../components/common/ModalsProvider.js";
import { SenhaLinkModal, type SenhaLinkResultado } from "../components/domain/SenhaLinkModal.js";
import { ErpShell } from "../components/layout/ErpShell.js";
import { useAuth } from "../hooks/useAuth.js";
import { formatMoney, formatPct } from "../lib/format-fr.js";
import { useT } from "../i18n/index.js";
import { trpc } from "../lib/trpc.js";
import { useAbaPendente } from "../lib/open-file.js";

/**
 * F7.2 — Detalhe do técnico.
 * 5 abas: Resumo · Dossiers · Faturas técnico · Pagamentos · Audit.
 */

type TabKey = "resumo" | "worklog" | "dossiers" | "faturas" | "pagamentos" | "audit" | "recados";

type Period = "since" | "30d" | "3m" | "6m" | "12m" | "year" | "all" | "custom";
/** CEO 2026-10-11: início do Gestion — janela padrão. */
const GESTION_START = "2026-05-01";

const PERIOD_ORDER: Period[] = ["since", "30d", "3m", "6m", "12m", "year", "all", "custom"];

function periodRange(p: Period): { periodStart?: string; periodEnd?: string } {
  // "custom" é resolvido pelo componente (datas digitadas).
  if (p === "all" || p === "custom") return {};
  const now = new Date();
  const end = now.toISOString().slice(0, 10);
  if (p === "since") return { periodStart: GESTION_START, periodEnd: end };
  if (p === "year") {
    return {
      periodStart: `${now.getFullYear()}-01-01`,
      periodEnd: end,
    };
  }
  const start = new Date(now);
  if (p === "30d") start.setDate(start.getDate() - 30);
  else if (p === "3m") start.setMonth(start.getMonth() - 3);
  else if (p === "6m") start.setMonth(start.getMonth() - 6);
  else start.setMonth(start.getMonth() - 12);
  return {
    periodStart: start.toISOString().slice(0, 10),
    periodEnd: end,
  };
}

const TI_STATUS_TONE: Record<string, string> = {
  draft: "bg-neutral-700/40 text-neutral-300",
  received: "bg-sky-500/15 text-sky-300",
  approved: "bg-cyan-500/15 text-cyan-300",
  rejected: "bg-amber-500/15 text-amber-300",
  paid: "bg-lime/15 text-lime",
  cancelled: "bg-red-500/15 text-red-300",
};
const DOSSIER_STATUS_TONE: Record<string, string> = {
  pending: "bg-neutral-700/40 text-neutral-300",
  issued: "bg-sky-500/15 text-sky-300",
  paid: "bg-lime/15 text-lime",
  cancelled: "bg-red-500/15 text-red-300",
};
export default function TechnicianDetail() {
  const params = useParams<{ id: string }>();
  const technicianId = Number(params.id);
  const { user } = useAuth();
  const t = useT();
  // CEO 2026-09-13: o worklog é a cara do técnico — abre direto nele.
  const [tab, setTab] = useState<TabKey>("worklog");
  const [period, setPeriod] = useState<Period>("since");
  // CEO 2026-10-11: além dos presets, datas livres.
  const hoje = new Date().toISOString().slice(0, 10);
  const [customFrom, setCustomFrom] = useState(`${hoje.slice(0, 7)}-01`);
  const [customTo, setCustomTo] = useState(hoje);

  const periodLabels: Record<Period, string> = {
    since: t("technicianDetail.periodSince"),
    "30d": t("technicianDetail.period30d"),
    "3m": t("technicianDetail.period3m"),
    "6m": t("technicianDetail.period6m"),
    "12m": t("technicianDetail.period12m"),
    year: t("technicianDetail.periodYear"),
    all: t("technicianDetail.periodAll"),
    custom: t("technicianDetail.periodCustom"),
  };
  const tabs: { key: TabKey; label: string }[] = [
    { key: "worklog", label: t("technicianDetail.tabWorklog") },
    { key: "resumo", label: t("technicianDetail.tabResumo") },
    { key: "dossiers", label: t("technicianDetail.dossiers") },
    { key: "faturas", label: t("technicianDetail.tabFaturas") },
    { key: "pagamentos", label: t("technicianDetail.tabPagamentos") },
    { key: "audit", label: t("technicianDetail.tabAudit") },
  ];
  const tabsVisiveis = [
    ...tabs,
    { key: "recados" as TabKey, label: `📣 ${t("technicianDetail.tabNotices")}` },
  ];

  const companiesQuery = trpc.companies.list.useQuery();
  const accessibleCompanies = (companiesQuery.data ?? []).filter((c) =>
    (user?.allowedCompanyIds ?? []).includes(c.id),
  );
  const candidateCompanies = useMemo(
    () => accessibleCompanies.map((c) => c.id),
    [accessibleCompanies],
  );
  const probeQueries = trpc.useQueries((t) =>
    candidateCompanies.map((cid) =>
      t.technicians.getById(
        { companyId: cid, technicianId },
        { retry: false, refetchOnMount: false, enabled: !Number.isNaN(technicianId) },
      ),
    ),
  );
  const probeIndex = probeQueries.findIndex((q) => q.data != null);
  const companyId: number | null =
    probeIndex >= 0 ? (candidateCompanies[probeIndex] ?? null) : null;
  const technician = probeIndex >= 0 ? (probeQueries[probeIndex]?.data ?? null) : null;

  const range = useMemo(
    () =>
      period === "custom"
        ? customFrom && customTo && customFrom <= customTo
          ? { periodStart: customFrom, periodEnd: customTo }
          : {}
        : periodRange(period),
    [period, customFrom, customTo],
  );

  if (Number.isNaN(technicianId)) {
    return (
      <Shell>
        <p className="text-sm text-red-400">{t("technicianDetail.invalidId")}</p>
      </Shell>
    );
  }
  if (!user) return null;

  const allLoading = probeQueries.length > 0 && probeQueries.every((q) => q.isLoading);
  if (allLoading) {
    return (
      <Shell>
        <p className="text-sm text-neutral-400">{t("technicianDetail.loading")}</p>
      </Shell>
    );
  }
  if (!technician || companyId === null) {
    return (
      <Shell>
        <p className="text-sm text-red-400">{t("technicianDetail.notFound")}</p>
        <Link href="/tecnicos" className="text-sm text-lime hover:text-lime">
          ← {t("technicianDetail.backToList")}
        </Link>
      </Shell>
    );
  }

  const isAdmin =
    user.isSuperAdmin ||
    user.memberships.some((m) => m.companyId === companyId && m.active && m.role === "admin");

  return (
    <Shell>
      <Header technician={technician} companyId={companyId} isAdmin={isAdmin} />

      <div className="rounded-lg border border-neutral-800 bg-neutral-900 px-4 py-3 flex items-center gap-3 flex-wrap">
        <label className="text-xs uppercase tracking-wider text-neutral-500">
          {t("technicianDetail.window")}:
        </label>
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value as Period)}
          className={inputClass + " w-48"}
        >
          {PERIOD_ORDER.map((p) => (
            <option key={p} value={p}>
              {periodLabels[p]}
            </option>
          ))}
        </select>
        {period === "custom" && (
          <>
            <input
              type="date"
              value={customFrom}
              max={customTo}
              onChange={(e) => setCustomFrom(e.target.value)}
              className={inputClass + " w-40"}
              data-testid="period-from"
            />
            <span className="text-xs text-neutral-500">→</span>
            <input
              type="date"
              value={customTo}
              min={customFrom}
              onChange={(e) => setCustomTo(e.target.value)}
              className={inputClass + " w-40"}
              data-testid="period-to"
            />
          </>
        )}
        {period !== "custom" && range.periodStart && (
          <span className="text-xs text-neutral-500">
            {range.periodStart} → {range.periodEnd}
          </span>
        )}
      </div>

      <nav className="rounded-lg border border-neutral-800 bg-neutral-900 px-2 py-1 flex flex-wrap gap-1">
        {tabsVisiveis
          .filter((tb) => tb.key !== "recados" || isAdmin)
          .map((tb) => (
            <button
              key={tb.key}
              onClick={() => setTab(tb.key)}
              className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
                tab === tb.key
                  ? "bg-lime/20 text-lime border border-lime/40"
                  : "hover:bg-neutral-800 text-neutral-400 border border-transparent"
              }`}
            >
              {tb.label}
            </button>
          ))}
      </nav>

      {tab === "resumo" && (
        <TabResumo
          companyId={companyId}
          technicianId={technician.id}
          technician={technician}
          range={range}
        />
      )}
      {tab === "worklog" && (
        <TabWorklog companyId={companyId} technicianId={technician.id} range={range} />
      )}
      {tab === "dossiers" && (
        <TabDossiers companyId={companyId} technicianId={technician.id} range={range} />
      )}
      {tab === "faturas" && (
        <TabFaturas companyId={companyId} technicianId={technician.id} range={range} />
      )}
      {tab === "pagamentos" && (
        <TabPagamentos companyId={companyId} technicianId={technician.id} range={range} />
      )}
      {tab === "audit" && (
        <TabAudit companyId={companyId} technicianId={technician.id} isAdmin={isAdmin} />
      )}
      {tab === "recados" && isAdmin && (
        <TabRecados companyId={companyId} technicianId={technician.id} />
      )}
    </Shell>
  );
}

// ============================================================================
// HEADER
// ============================================================================

function Header({
  technician,
  companyId,
  isAdmin,
}: {
  technician: {
    id: number;
    name: string;
    email: string | null;
    active: boolean;
    portalBlocked?: boolean;
    userId: number | null;
    parcServiceRole: "debosselage" | "montage" | null;
  };
  companyId: number;
  isAdmin: boolean;
}) {
  const t = useT();
  const [criarAcesso, setCriarAcesso] = useState(false);
  return (
    <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-4 flex items-start justify-between">
      <div>
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-semibold">{technician.name}</h2>
          {!technician.active && (
            <span className="rounded px-2 py-0.5 text-xs bg-neutral-700/40 text-neutral-400">
              {t("technicianDetail.inactive")}
            </span>
          )}
          {technician.portalBlocked && (
            <span className="badge b-warn whitespace-nowrap" data-testid="tech-portal-blocked">
              🔒 {t("technicianDetail.noticesBlockedBadge")}
            </span>
          )}
        </div>
        <p className="text-xs text-neutral-500 mt-1">{technician.email ?? "—"}</p>
      </div>
      <div className="flex items-center gap-3">
        {/* F-tech-pares-atalho (CEO 2026-09-24, Roberto Alejandro Moya): os
            pares técnico×cliente/chantier se configuram no "modifier" da lista
            — quem chega por aqui não achava o caminho. Atalho direto. */}
        {/* F-admin-usuarios (CEO 2026-11-02) — técnico sem conta (veio do eDSP):
            cria o acesso ao portal ligado a ESTA ficha, sem duplicar. */}
        {isAdmin && technician.active && technician.userId == null && (
          <button
            type="button"
            onClick={() => setCriarAcesso(true)}
            className="btn text-xs"
            data-testid="tech-create-portal-access"
          >
            🔑 {t("technicianDetail.createPortalAccess")}
          </button>
        )}
        <Link
          href={`/tecnicos?editar=${technician.id}`}
          className="btn btn--primary text-xs"
          data-testid="configure-commissions"
        >
          {t("technicianDetail.configureCommissions")}
        </Link>
        <Link href="/tecnicos" className="text-sm text-neutral-400 hover:text-lime">
          ← {t("technicianDetail.list")}
        </Link>
      </div>
      {criarAcesso && (
        <CriarAcessoPortal
          technician={technician}
          companyId={companyId}
          onClose={() => setCriarAcesso(false)}
        />
      )}
    </div>
  );
}

/**
 * F-admin-usuarios — janela "Criar acesso ao portal": e-mail (da ficha),
 * idioma e função; o Gestion cria a conta ligada à ficha e manda o link
 * "defina sua senha". Depois mostra o resultado com "Copiar link".
 */
function CriarAcessoPortal({
  technician,
  companyId,
  onClose,
}: {
  technician: {
    id: number;
    name: string;
    email: string | null;
    parcServiceRole: "debosselage" | "montage" | null;
  };
  companyId: number;
  onClose: () => void;
}) {
  const t = useT();
  const utils = trpc.useUtils();
  const [email, setEmail] = useState(technician.email ?? "");
  const [locale, setLocale] = useState<"fr" | "pt" | "en" | "es" | "uk">("pt");
  const [funcao, setFuncao] = useState<"debosselage" | "montage">(
    technician.parcServiceRole ?? "debosselage",
  );
  const [resultado, setResultado] = useState<SenhaLinkResultado | null>(null);
  const criar = trpc.users.createWithLink.useMutation({
    onSuccess: (r) => {
      void utils.technicians.getById.invalidate();
      setResultado({
        titulo: t("users.linkSentTitle"),
        nome: r.name,
        email: r.email,
        emailEnviado: r.emailEnviado,
        link: r.link,
      });
    },
  });
  if (resultado) return <SenhaLinkModal resultado={resultado} onClose={onClose} />;
  const campo =
    "w-full rounded-md border border-[var(--border)] bg-[var(--surface-page)] px-3 py-1.5 text-sm text-[var(--text-body)] outline-none focus:border-lime";
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="w-full max-w-md space-y-3 rounded-xl border border-[var(--border-strong)] bg-[var(--surface-card)] p-5 shadow-[var(--shadow-sm)]"
        onClick={(e) => e.stopPropagation()}
        data-testid="tech-portal-access-modal"
      >
        <h2 className="text-lg font-bold text-[var(--text-strong)]">
          {t("technicianDetail.createPortalAccess")}
        </h2>
        <p className="text-sm text-[var(--text-body)]">
          <strong>{technician.name}</strong>
        </p>
        <label className="block text-xs text-[var(--text-muted)]">
          {t("users.emailLabel")}
          <input
            type="email"
            autoComplete="off"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={campo + " mt-1"}
            data-testid="tech-access-email"
          />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-xs text-[var(--text-muted)]">
            {t("users.localeLabel")}
            <select
              value={locale}
              onChange={(e) => setLocale(e.target.value as typeof locale)}
              className={campo + " mt-1"}
              data-testid="tech-access-locale"
            >
              <option value="pt">Português</option>
              <option value="fr">Français</option>
              <option value="es">Español</option>
              <option value="en">English</option>
              <option value="uk">Українська</option>
            </select>
          </label>
          <label className="block text-xs text-[var(--text-muted)]">
            {t("technicianDetail.portalRoleLabel")}
            <select
              value={funcao}
              onChange={(e) => setFuncao(e.target.value as typeof funcao)}
              className={campo + " mt-1"}
              data-testid="tech-access-role"
            >
              <option value="debosselage">{t("users.roleTechnicianPdr")}</option>
              <option value="montage">{t("users.roleTechnicianMontador")}</option>
            </select>
          </label>
        </div>
        <p className="text-xs text-[var(--text-muted)]">{t("users.createLinkHint")}</p>
        {criar.error && (
          <p className="text-sm text-[var(--danger-500)]" data-testid="tech-access-error">
            {criar.error.message}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-[var(--border-strong)] px-3 py-1.5 text-sm text-[var(--text-body)]"
          >
            {t("users.cancelBtn")}
          </button>
          <button
            type="button"
            disabled={criar.isPending || !email.trim()}
            onClick={() =>
              criar.mutate({
                name: technician.name,
                email: email.trim(),
                locale,
                memberships: [{ companyId, role: "technician", parcServiceRole: funcao }],
                technicianId: technician.id,
              })
            }
            className="rounded-md bg-lime px-4 py-1.5 text-sm font-medium text-on-lime disabled:opacity-50"
            data-testid="tech-access-submit"
          >
            {criar.isPending ? t("users.creatingBtn") : t("users.createLinkBtn")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// TAB: RESUMO
// ============================================================================

function TabResumo({
  companyId,
  technicianId,
  technician,
  range,
}: {
  companyId: number;
  technicianId: number;
  technician: {
    phone: string | null;
    taxId: string | null;
    countryCode: string | null;
  };
  range: { periodStart?: string; periodEnd?: string };
}) {
  const opQuery = trpc.technicians.getOperationalSummary.useQuery({
    companyId,
    technicianId,
    ...range,
  });
  const finQuery = trpc.technicians.getFinancialSummary.useQuery({
    companyId,
    technicianId,
    ...range,
  });
  // F-tcc2 — comissão é por par técnico×cliente; mostra os pares no cadastro.
  const pairsQuery = trpc.technicians.listClientCommissions.useQuery({
    companyId,
    technicianId,
  });
  const t = useT();
  const op = opQuery.data;
  const fin = finQuery.data;
  const avgFat = op && op.lineCount > 0 ? op.totalFaturado / op.lineCount : null;

  return (
    <div className="grid grid-cols-2 gap-4">
      <section className="rounded-lg border border-neutral-800 bg-neutral-900 p-4">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-500 mb-3">
          {t("technicianDetail.registration")}
        </h3>
        <dl className="space-y-1.5 text-sm">
          <Row label={t("technicianDetail.country")} value={technician.countryCode ?? "—"} />
          <Row label={t("technicianDetail.phone")} value={technician.phone ?? "—"} />
          <Row label={t("technicianDetail.taxId")} value={technician.taxId ?? "—"} mono />
          <Row
            label={t("technicianDetail.commissionsByCustomer")}
            value={
              pairsQuery.data == null
                ? "…"
                : pairsQuery.data.length === 0
                  ? t("technicianDetail.noPairConfigured")
                  : pairsQuery.data
                      .map(
                        (p) =>
                          `${p.customerName} ${Number(p.commissionPct).toFixed(0)}% ${p.commissionBase}`,
                      )
                      .join(" · ")
            }
          />
        </dl>
      </section>

      <section className="rounded-lg border border-neutral-800 bg-neutral-900 p-4">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-500 mb-3">
          {t("technicianDetail.operational")}
        </h3>
        <dl className="space-y-1.5 text-sm">
          <Row label={t("technicianDetail.linesHandled")} value={op ? String(op.lineCount) : "…"} />
          <Row label={t("technicianDetail.dossiers")} value={op ? String(op.dossierCount) : "…"} />
          <Row
            label={t("technicianDetail.invoicedHtLines")}
            value={op ? formatMoney(op.totalFaturado, { decimals: 2 }) : "…"}
            mono
          />
          <Row
            label={t("technicianDetail.avgInvoicedPerLine")}
            value={avgFat != null ? formatMoney(avgFat, { decimals: 2 }) : "—"}
            mono
          />
          {op && Object.keys(op.byServiceType).length > 0 && (
            <div className="flex gap-2 pt-1 flex-wrap">
              {Object.entries(op.byServiceType).map(([type, count]) => (
                <span
                  key={type}
                  className="rounded px-2 py-0.5 text-xs bg-neutral-800 text-neutral-400"
                >
                  {type}: {count}
                </span>
              ))}
            </div>
          )}
        </dl>
      </section>

      <section className="rounded-lg border border-neutral-800 bg-neutral-900 p-4 col-span-2">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-500 mb-3">
          {t("technicianDetail.financial")}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <KpiBlock
            label={t("technicianDetail.tabFaturas")}
            value={fin ? String(fin.tiCount) : "…"}
          />
          <KpiBlock
            label={t("technicianDetail.totalIssuedByTechnician")}
            value={fin ? formatMoney(fin.tiTotalAmount, { decimals: 2 }) : "…"}
          />
          <KpiBlock
            label={t("technicianDetail.totalPaid")}
            value={fin ? formatMoney(fin.paidAmount, { decimals: 2 }) : "…"}
          />
          <KpiBlock
            label={t("technicianDetail.toPay")}
            value={fin ? formatMoney(fin.pendingAmount, { decimals: 2 }) : "…"}
            tone={
              fin && fin.pendingAmount > 0
                ? "amber"
                : fin && fin.pendingAmount === 0
                  ? "positive"
                  : undefined
            }
          />
          <KpiBlock
            label={t("technicianDetail.avgDaysToPayment")}
            value={fin?.avgDaysToPayment != null ? `${fin.avgDaysToPayment.toFixed(1)} d` : "—"}
          />
          <KpiBlock
            label={t("technicianDetail.approvalRate")}
            value={fin?.approvalRate != null ? formatPct(fin.approvalRate) : "—"}
          />
        </div>
      </section>
    </div>
  );
}

// ============================================================================
// TAB: WORKLOG (F-tech-worklog)
// ============================================================================

function TabWorklog({
  companyId,
  technicianId,
  range,
}: {
  companyId: number;
  technicianId: number;
  range: { periodStart?: string; periodEnd?: string };
}) {
  const t = useT();
  const wlQuery = trpc.technicians.worklog.useQuery({
    companyId,
    technicianId,
    ...range,
  });
  const wl = wlQuery.data;
  // CEO 2026-09-13: UMA linha por carro — serviços viram chips na mesma
  // linha, comissão SOMADA (mesmo padrão da lista de Pagamentos de técnicos).
  type WlGroup = {
    dossierId: number;
    date: string;
    dossierCode: string;
    plate: string | null;
    vin: string | null;
    make: string | null;
    model: string | null;
    customerName: string;
    endCustomerName: string | null;
    atelierName: string | null;
    customerInvoiceStatus: string;
    services: string[];
    produced: number;
    clientReceived: number;
    commission: number;
    hasCommission: boolean;
    hasTbd: boolean;
    allPaid: boolean;
    /** CEO 2026-10-26 (FJ-929-KH) — quanto do carro já foi pago ao técnico e quanto falta. */
    paidAmount: number;
    pendingAmount: number;
    pairConfigured: boolean;
    /** CEO 2026-10-26 — faturas de cliente do carro ("F2026-00052, …"). */
    invoiceNumbers: string | null;
    /** Último recebimento do cliente no carro. */
    clientPaidAt: string | null;
    /** Proforma que pagou o técnico neste carro e a data. */
    techPaidRef: string | null;
    techPaidAt: string | null;
  };
  // CEO 2026-10-11: os cards não tinham ação — agora filtram a lista.
  type CardFilter = null | "clientReceived" | "toReceive" | "released" | "awaiting" | "paid";
  const [cardFilter, setCardFilter] = useState<CardFilter>(null);
  const cardCls = (k: Exclude<CardFilter, null> | "produced") =>
    `rounded-lg border bg-neutral-900 p-4 text-left transition ${
      cardFilter != null && cardFilter === k
        ? "border-lime ring-1 ring-lime"
        : "border-neutral-800 hover:border-neutral-600"
    }`;
  const alternar = (k: Exclude<CardFilter, null>) =>
    setCardFilter((atual) => (atual === k ? null : k));
  const rows = useMemo(() => {
    const byDossier = new Map<number, WlGroup>();
    for (const r of wlQuery.data?.rows ?? []) {
      const g = byDossier.get(r.dossierId);
      if (!g) {
        byDossier.set(r.dossierId, {
          dossierId: r.dossierId,
          date: r.date,
          dossierCode: r.dossierCode,
          plate: r.plate,
          vin: r.vin,
          make: r.make,
          model: r.model,
          customerName: r.customerName,
          endCustomerName: r.endCustomerName,
          atelierName: r.atelierName,
          customerInvoiceStatus: r.customerInvoiceStatus,
          services: [r.serviceType],
          // F-worklog-remise — já líquido da remise do cliente (vem do backend).
          produced: r.produced,
          clientReceived: r.clientReceived,
          commission: r.commission ?? 0,
          hasCommission: r.commission != null,
          hasTbd: r.commission == null,
          allPaid: r.paymentStatus === "paid",
          paidAmount: r.paymentStatus === "paid" ? (r.commission ?? 0) : 0,
          pendingAmount: r.paymentStatus === "pending" ? (r.commission ?? 0) : 0,
          pairConfigured: r.pairConfigured,
          invoiceNumbers: r.invoiceNumbers,
          clientPaidAt: r.clientPaidAt,
          techPaidRef: r.techPaidRef,
          techPaidAt: r.techPaidAt,
        });
      } else {
        if (!g.services.includes(r.serviceType)) g.services.push(r.serviceType);
        g.produced += r.produced;
        g.clientReceived += r.clientReceived;
        if (r.commission != null) {
          g.commission += r.commission;
          g.hasCommission = true;
        } else g.hasTbd = true;
        g.allPaid = g.allPaid && r.paymentStatus === "paid";
        if (r.paymentStatus === "paid") g.paidAmount += r.commission ?? 0;
        else if (r.paymentStatus === "pending") g.pendingAmount += r.commission ?? 0;
        if (r.date > g.date) g.date = r.date;
      }
    }
    return Array.from(byDossier.values());
  }, [wlQuery.data]);
  const rowsFiltradas = useMemo(
    () =>
      rows.filter((r) => {
        switch (cardFilter) {
          case "paid":
            return r.allPaid;
          case "released":
            return !r.allPaid && r.customerInvoiceStatus === "paid";
          case "awaiting":
            return !r.allPaid && r.customerInvoiceStatus !== "paid";
          case "toReceive":
            return !r.allPaid;
          case "clientReceived":
            return r.clientReceived > 0;
          default:
            return true;
        }
      }),
    [rows, cardFilter],
  );

  const noPairCustomers = (wl?.byCustomer ?? []).filter((c) => !c.pairConfigured);

  return (
    <div className="space-y-4">
      {/* F-worklog — exportação semanal (PDF/e-mail) do documento do
          CLIENTE, sem comissões. */}
      <WorklogExportCard companyId={companyId} technicianId={technicianId} />
      {/* F-worklog-no-pair — comissão 0 quase sempre = par técnico×cliente
          faltando; banner com o caminho do fix (cores fixas p/ tema claro). */}
      {noPairCustomers.length > 0 && (
        <div
          role="alert"
          style={{
            background: "#FEF9C3",
            border: "1px solid #CA8A04",
            borderRadius: 8,
            padding: "10px 14px",
            color: "#854D0E",
          }}
          className="flex flex-wrap items-center justify-between gap-2 text-sm"
        >
          <span>
            ⚠ {t("technicianDetail.wlNoPairPre")}{" "}
            <strong>{noPairCustomers.map((c) => c.customerName).join(", ")}</strong>.{" "}
            {t("technicianDetail.wlNoPairSuf")}
          </span>
          <Link
            href={`/tecnicos?editar=${technicianId}`}
            style={{ background: "#CA8A04", color: "#fff" }}
            className="shrink-0 rounded-md px-3 py-1 text-xs font-bold"
          >
            {t("technicianDetail.wlNoPairBtn")} »
          </Link>
        </div>
      )}

      {/* F-worklog-producao (CEO 2026-09-27) — produção → recebido do cliente →
          a repassar → falta. Valores do CLIENTE em HT; os demais, comissão. */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
        <button type="button" onClick={() => setCardFilter(null)} className={cardCls("produced")}>
          <div className="text-xs text-neutral-500">{t("technicianDetail.wlProduced")}</div>
          <div className="font-mono text-lg">
            {wl ? formatMoney(wl.summary.produced, { decimals: 2 }) : "…"}
          </div>
          <div className="text-[10px] text-neutral-500">{t("technicianDetail.wlProducedSub")}</div>
        </button>
        <button
          type="button"
          onClick={() => alternar("clientReceived")}
          className={cardCls("clientReceived")}
        >
          <div className="text-xs text-neutral-500">{t("technicianDetail.wlClientReceived")}</div>
          <div className="font-mono text-lg">
            {wl ? formatMoney(wl.summary.clientReceived, { decimals: 2 }) : "…"}
          </div>
          {wl != null && wl.summary.produced > 0 && (
            <div className="text-[10px] text-neutral-500">
              {Math.round((wl.summary.clientReceived / wl.summary.produced) * 100)}%{" "}
              {t("technicianDetail.wlOfProduced")}
            </div>
          )}
        </button>
        <button
          type="button"
          onClick={() => alternar("toReceive")}
          className={cardCls("toReceive")}
        >
          <div className="text-xs text-neutral-500">{t("technicianDetail.wlToReceive")}</div>
          <div className="font-mono text-lg" style={{ color: "#D97706" }}>
            {wl ? formatMoney(wl.summary.toReceive, { decimals: 2 }) : "…"}
          </div>
          {wl != null && wl.summary.pendingCalc > 0 && (
            <div className="text-[10px] text-neutral-500">
              +{wl.summary.pendingCalc} {t("technicianDetail.wlPendingCalc")}
            </div>
          )}
        </button>
        <button type="button" onClick={() => alternar("released")} className={cardCls("released")}>
          <div className="text-xs text-neutral-500">{t("technicianDetail.wlReleased")}</div>
          <div className="font-mono text-lg text-lime">
            {wl ? formatMoney(wl.summary.released, { decimals: 2 }) : "…"}
          </div>
        </button>
        <button type="button" onClick={() => alternar("awaiting")} className={cardCls("awaiting")}>
          <div className="text-xs text-neutral-500">{t("technicianDetail.wlAwaitingClient")}</div>
          <div className="font-mono text-lg text-neutral-300">
            {wl ? formatMoney(wl.summary.awaitingClient, { decimals: 2 }) : "…"}
          </div>
        </button>
        <button type="button" onClick={() => alternar("paid")} className={cardCls("paid")}>
          <div className="text-xs text-neutral-500">{t("technicianDetail.wlReceived")}</div>
          <div className="font-mono text-lg">
            {wl ? formatMoney(wl.summary.received, { decimals: 2 }) : "…"}
          </div>
          {wl != null && (
            <div className="text-[10px] text-neutral-500">
              {wl.summary.cars} {t("technicianDetail.wlCarsWord")}
            </div>
          )}
        </button>
      </div>
      <p className="text-[11px] text-neutral-500">
        {t("technicianDetail.wlCardFilterHint")}
        {cardFilter != null && (
          <>
            {" · "}
            <button type="button" onClick={() => setCardFilter(null)} className="underline">
              {t("technicianDetail.wlClearFilter")}
            </button>{" "}
            ({rowsFiltradas.length}/{rows.length})
          </>
        )}
      </p>

      {wl != null && wl.summary.released > 0 && (
        <Link
          href={`/pagamentos-tecnicos?tecnico=${technicianId}&liberados=1`}
          className="inline-block rounded-md bg-lime px-4 py-1.5 text-sm font-medium text-on-lime hover:bg-lime-bright"
        >
          💸 {t("technicianDetail.wlPayBtn")}
        </Link>
      )}

      {/* CA por cliente */}
      {wl != null && wl.byCustomer.length > 0 && (
        <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-4">
          <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-neutral-500">
            {t("technicianDetail.wlByCustomer")}
          </h3>
          {/* CEO 2026-10-11: chips numa linha só ficavam ilegíveis — um
              mini-card por cliente: nome, carros/produzido/encaissé, comissão. */}
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {wl.byCustomer.map((c) => (
              <div
                key={c.customerId}
                className="flex items-center justify-between gap-3 rounded-md border border-neutral-800 bg-neutral-950/50 px-3 py-2"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium" title={c.customerName}>
                    {!c.pairConfigured && (
                      <span title={t("technicianDetail.wlNoPairChip")}>⚠ </span>
                    )}
                    {c.customerName}
                  </div>
                  <div className="whitespace-nowrap text-[11px] text-neutral-500">
                    {c.cars} 🚗 · {formatMoney(c.produced, { decimals: 0 })}{" "}
                    {t("technicianDetail.wlProducedShort")} ·{" "}
                    {formatMoney(c.clientReceived, { decimals: 0 })}{" "}
                    {t("technicianDetail.wlReceivedShort")}
                  </div>
                </div>
                <div className="shrink-0 font-mono text-sm tabular-nums">
                  {formatMoney(c.commission, { decimals: 2 })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Diário linha a linha */}
      <div className="overflow-hidden rounded-lg border border-neutral-800 bg-neutral-900">
        {wlQuery.isLoading && (
          <p className="p-4 text-sm text-neutral-500">{t("technicianDetail.loading")}</p>
        )}
        {wlQuery.error && <p className="p-4 text-sm text-red-400">{wlQuery.error.message}</p>}
        {wl != null && rowsFiltradas.length === 0 && (
          <p className="p-4 text-sm text-neutral-500">{t("technicianDetail.wlEmpty")}</p>
        )}
        {rowsFiltradas.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-950/50 text-xs uppercase tracking-wider text-neutral-500">
                <tr>
                  <th className="whitespace-nowrap px-3 py-2 text-left">
                    {t("technicianDetail.colDate")}
                  </th>
                  <th className="min-w-36 px-3 py-2 text-left">{t("technicianDetail.wlColCar")}</th>
                  <th className="min-w-40 px-3 py-2 text-left">
                    {t("technicianDetail.colCustomer")}
                  </th>
                  <th className="px-3 py-2 text-left">{t("technicianDetail.wlColService")}</th>
                  <th className="whitespace-nowrap px-3 py-2 text-right">
                    {t("technicianDetail.wlColProduced")}
                  </th>
                  <th className="max-w-28 px-3 py-2 text-right">
                    {t("technicianDetail.wlColClientReceived")}
                  </th>
                  <th className="px-3 py-2 text-right">{t("technicianDetail.wlColCommission")}</th>
                  <th className="whitespace-nowrap px-3 py-2 text-left">
                    {t("technicianDetail.colStatus")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-800">
                {rowsFiltradas.map((r) => (
                  <tr key={r.dossierId}>
                    <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-neutral-400">
                      {r.date}
                    </td>
                    {/* CEO 2026-10-11: a placa quebrava em 3 linhas e o modelo
                        espremia o resto — placa inteira numa linha, modelo embaixo. */}
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5">
                        <CarLogo make={r.make} size={20} />
                        <div className="min-w-0">
                          <Link
                            href={`/dossiers/${r.dossierId}`}
                            className="whitespace-nowrap font-mono text-xs text-lime hover:underline"
                          >
                            {r.plate ?? r.vin ?? r.dossierCode}
                          </Link>
                          <div className="truncate text-[11px] text-neutral-500">
                            {[r.make, r.model].filter(Boolean).join(" ")}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="max-w-64 px-3 py-2 text-xs text-neutral-400">
                      <div title={r.customerName}>{r.customerName}</div>
                      {/* F-worklog-atelier — ateliê (cliente do cliente), como em /dossiers */}
                      {r.endCustomerName && (
                        <div className="text-[11px] text-neutral-500">↳ {r.endCustomerName}</div>
                      )}
                      {r.atelierName && (
                        <div className="text-[11px] text-neutral-500">🏭 {r.atelierName}</div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span className="inline-flex flex-wrap gap-1">
                        {r.services.map((s) => (
                          <span
                            key={s}
                            className="rounded bg-neutral-800 px-1.5 py-0.5 text-[10px] text-neutral-300"
                          >
                            {s === "dsp"
                              ? "PDR"
                              : s === "share_demontage"
                                ? t("technicianDetail.wlShareDemontage")
                                : s === "share_montage"
                                  ? t("technicianDetail.wlShareMontage")
                                  : /montage/i.test(s)
                                    ? t("technicianDetail.wlMontage")
                                    : s}
                          </span>
                        ))}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-xs">
                      {formatMoney(r.produced, { decimals: 2 })}
                    </td>
                    <td
                      className="whitespace-nowrap px-3 py-2 text-right font-mono text-xs"
                      style={{
                        color:
                          r.clientReceived >= r.produced - 0.01 && r.produced > 0
                            ? "#16A34A"
                            : undefined,
                      }}
                    >
                      {r.clientReceived > 0 ? formatMoney(r.clientReceived, { decimals: 2 }) : "—"}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-xs">
                      {!r.pairConfigured ? (
                        <span
                          title={t("technicianDetail.wlNoPairChip")}
                          className="rounded px-1.5 py-0.5 font-sans text-[10px] font-semibold"
                          style={{ background: "#FEF9C3", color: "#854D0E" }}
                        >
                          ⚠ {t("technicianDetail.wlNoPairShort")}
                        </span>
                      ) : r.hasCommission ? (
                        <>
                          <span className="whitespace-nowrap">
                            {formatMoney(r.commission, { decimals: 2 })}
                          </span>
                          {r.hasTbd && (
                            <span className="ml-1 text-neutral-600">
                              + {t("technicianDetail.wlTbd")}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-neutral-600">{t("technicianDetail.wlTbd")}</span>
                      )}
                    </td>
                    {/* CEO 2026-10-26 — dois lados: cliente (a faturar / faturado
                        com o nº / recebido) e técnico (aguardando / liberado /
                        pago, com a proforma — "pago antes do cliente" quando o
                        técnico recebeu primeiro). */}
                    <td className="px-3 py-2" data-testid={`wl-status-${r.dossierId}`}>
                      <div className="flex flex-col items-start gap-1">
                        {r.customerInvoiceStatus === "paid" ? (
                          <span
                            className="badge b-ok whitespace-nowrap"
                            title={r.clientPaidAt ? formatDateFr(r.clientPaidAt) : undefined}
                          >
                            {t("technicianDetail.wlClientPaidBadge")}
                            {r.invoiceNumbers ? ` · ${r.invoiceNumbers}` : ""}
                          </span>
                        ) : r.invoiceNumbers ? (
                          <span className="badge b-info whitespace-nowrap">
                            {t("technicianDetail.wlClientInvoiced")} · {r.invoiceNumbers}
                          </span>
                        ) : (
                          <span className="badge b-mut whitespace-nowrap">
                            {t("technicianDetail.wlClientToInvoice")}
                          </span>
                        )}
                        {r.allPaid ? (
                          <span
                            className={`badge whitespace-nowrap ${pagoAntesDoCliente(r) ? "b-violet" : "b-ok"}`}
                            title={
                              pagoAntesDoCliente(r)
                                ? t("technicianDetail.wlPaidBeforeClientHint")
                                : undefined
                            }
                          >
                            ✓{" "}
                            {pagoAntesDoCliente(r)
                              ? t("technicianDetail.wlPaidBeforeClient")
                              : t("technicianDetail.statusPaid")}
                            {r.techPaidRef
                              ? ` · ${r.techPaidRef}${r.techPaidAt ? ` · ${formatDateFr(r.techPaidAt)}` : ""}`
                              : ""}
                          </span>
                        ) : r.paidAmount > 0.005 ? (
                          /* CEO 2026-10-26 (FJ-929-KH) — parte já paga (ex.: PDR
                             adiantado) + o que falta (ex.: metade de montagem
                             feita depois): dois selos, para "Libéré" não parecer
                             o carro inteiro a pagar de novo. */
                          <>
                            <span
                              className={`badge whitespace-nowrap ${pagoAntesDoCliente({ ...r, allPaid: true }) ? "b-violet" : "b-ok"}`}
                            >
                              ✓{" "}
                              {pagoAntesDoCliente({ ...r, allPaid: true })
                                ? t("technicianDetail.wlPaidBeforeClient")
                                : t("technicianDetail.statusPaid")}{" "}
                              {formatMoney(r.paidAmount, { decimals: 2 })}
                              {r.techPaidRef
                                ? ` · ${r.techPaidRef}${r.techPaidAt ? ` · ${formatDateFr(r.techPaidAt)}` : ""}`
                                : ""}
                            </span>
                            <span
                              className={`badge whitespace-nowrap ${r.customerInvoiceStatus === "paid" ? "b-warn" : "b-mut"}`}
                            >
                              {t("technicianDetail.wlRemaining")}{" "}
                              {formatMoney(r.pendingAmount, { decimals: 2 })} ·{" "}
                              {r.customerInvoiceStatus === "paid"
                                ? t("technicianDetail.wlStatusReleased")
                                : t("technicianDetail.wlStatusAwaiting")}
                            </span>
                          </>
                        ) : r.customerInvoiceStatus === "paid" ? (
                          <span className="badge b-warn whitespace-nowrap">
                            {t("technicianDetail.wlStatusReleased")}
                          </span>
                        ) : (
                          <span className="badge b-mut whitespace-nowrap">
                            {t("technicianDetail.wlStatusAwaiting")}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// TAB: DOSSIERS
// ============================================================================

function TabDossiers({
  companyId,
  technicianId,
  range,
}: {
  companyId: number;
  technicianId: number;
  range: { periodStart?: string; periodEnd?: string };
}) {
  const listQuery = trpc.technicians.listDossiers.useQuery({
    companyId,
    technicianId,
    ...range,
    limit: 200,
    offset: 0,
  });
  const rows = listQuery.data?.rows ?? [];
  const t = useT();
  const dossierStatusLabels: Record<string, string> = {
    pending: t("technicianDetail.statusPending"),
    issued: t("technicianDetail.statusIssued"),
    // Fatura de CLIENTE quitada = "recebida" (não confundir com pagar téc).
    paid: t("technicianDetail.statusCustomerReceived"),
    cancelled: t("technicianDetail.statusCancelled"),
  };

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-500">
          {t("technicianDetail.dossiersOfTechnician")}
        </h3>
        <span className="text-xs text-neutral-500">
          {listQuery.data?.total ?? 0} {t("technicianDetail.total")}
        </span>
      </div>

      {listQuery.isLoading && (
        <p className="text-sm text-neutral-500">{t("technicianDetail.loading")}</p>
      )}
      {rows.length === 0 && !listQuery.isLoading && (
        <p className="text-sm text-neutral-500 bg-neutral-900 border border-neutral-800 rounded-md p-4">
          {t("technicianDetail.emptyDossiers")}
        </p>
      )}

      <div className="rounded-lg border border-neutral-800 bg-neutral-900 overflow-hidden">
        {rows.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-neutral-950/50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="text-left px-3 py-2 w-32">{t("technicianDetail.colCode")}</th>
                <th className="text-left px-3 py-2 w-28">{t("technicianDetail.colDate")}</th>
                <th className="text-left px-3 py-2 w-36">
                  {t("technicianDetail.colInvoiceNumber")}
                </th>
                <th className="text-left px-3 py-2 w-32">{t("technicianDetail.colPlate")}</th>
                <th className="text-right px-3 py-2 w-20">{t("technicianDetail.colCustomer")}</th>
                <th className="text-left px-3 py-2 w-24">{t("technicianDetail.colStatus")}</th>
                <th className="px-3 py-2 w-20"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-800">
              {rows.map((d) => (
                <tr key={d.id}>
                  <td className="px-3 py-2 font-mono text-xs">
                    <Link href={`/dossiers/${d.id}`} className="text-lime hover:text-lime">
                      {d.dossierCode}
                    </Link>
                  </td>
                  <td className="px-3 py-2 text-xs text-neutral-400">{d.invoiceDate ?? "—"}</td>
                  <td className="px-3 py-2 text-xs font-mono text-neutral-400">
                    {d.invoiceNumberExternal ?? "—"}
                  </td>
                  <td className="px-3 py-2 text-xs font-mono text-neutral-400">{d.plate ?? "—"}</td>
                  <td className="px-3 py-2 text-right text-xs text-neutral-400">
                    <Link href={`/clientes/${d.customerId}`} className="hover:text-lime">
                      #{d.customerId}
                    </Link>
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-block rounded px-2 py-0.5 text-xs ${
                        DOSSIER_STATUS_TONE[d.customerInvoiceStatus] ?? ""
                      }`}
                    >
                      {dossierStatusLabels[d.customerInvoiceStatus] ?? d.customerInvoiceStatus}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Link
                      href={`/dossiers/${d.id}`}
                      className="rounded-md border border-neutral-800 hover:border-lime text-lime px-2 py-0.5 text-xs"
                    >
                      {t("technicianDetail.open")}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

// ============================================================================
// TAB: FATURAS TÉCNICO
// ============================================================================

function TabFaturas({
  companyId,
  technicianId,
  range,
}: {
  companyId: number;
  technicianId: number;
  range: { periodStart?: string; periodEnd?: string };
}) {
  const listQuery = trpc.technicianInvoices.list.useQuery({
    companyId,
    technicianId,
    ...(range.periodStart ? { periodFrom: range.periodStart } : {}),
    ...(range.periodEnd ? { periodTo: range.periodEnd } : {}),
    limit: 200,
    offset: 0,
  });
  const rows = listQuery.data?.rows ?? [];
  const t = useT();
  const tiStatusLabels: Record<string, string> = {
    draft: t("technicianDetail.statusDraft"),
    received: t("technicianDetail.statusReceived"),
    approved: t("technicianDetail.statusApproved"),
    rejected: t("technicianDetail.statusRejected"),
    paid: t("technicianDetail.statusPaid"),
    cancelled: t("technicianDetail.statusCancelled"),
  };

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-500">
          {t("technicianDetail.invoicesIssuedByTechnician")}
        </h3>
        <span className="text-xs text-neutral-500">
          {listQuery.data?.total ?? 0} {t("technicianDetail.total")}
        </span>
      </div>

      {listQuery.isLoading && (
        <p className="text-sm text-neutral-500">{t("technicianDetail.loading")}</p>
      )}
      {rows.length === 0 && !listQuery.isLoading && (
        <p className="text-sm text-neutral-500 bg-neutral-900 border border-neutral-800 rounded-md p-4">
          {t("technicianDetail.emptyInvoices")}
        </p>
      )}

      <div className="rounded-lg border border-neutral-800 bg-neutral-900 overflow-hidden">
        {rows.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-neutral-950/50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="text-left px-3 py-2 w-36">
                  {t("technicianDetail.colNumberExternal")}
                </th>
                <th className="text-left px-3 py-2 w-28">{t("technicianDetail.colIssueDate")}</th>
                <th className="text-left px-3 py-2 w-28">{t("technicianDetail.colPeriod")}</th>
                <th className="text-right px-3 py-2 w-32 font-mono">
                  {t("technicianDetail.colTotal")}
                </th>
                <th className="text-left px-3 py-2 w-24">{t("technicianDetail.colStatus")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-800">
              {rows.map((inv) => (
                <tr key={inv.id}>
                  <td className="px-3 py-2 font-mono text-xs">{inv.invoiceNumberExternal}</td>
                  <td className="px-3 py-2 text-xs text-neutral-400">{inv.issueDate ?? "—"}</td>
                  <td className="px-3 py-2 text-xs text-neutral-500">
                    {inv.periodStart && inv.periodEnd
                      ? `${inv.periodStart} → ${inv.periodEnd}`
                      : "—"}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {formatMoney(inv.totalAmount, { decimals: 2 })}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-block rounded px-2 py-0.5 text-xs ${
                        TI_STATUS_TONE[inv.docStatus] ?? ""
                      }`}
                    >
                      {tiStatusLabels[inv.docStatus] ?? inv.docStatus}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

// ============================================================================
// TAB: PAGAMENTOS
// ============================================================================

function TabPagamentos({
  companyId,
  technicianId,
  range,
}: {
  companyId: number;
  technicianId: number;
  range: { periodStart?: string; periodEnd?: string };
}) {
  // Lista TIs do técnico e busca payments de cada uma. Para volume baixo é ok.
  const tiQuery = trpc.technicianInvoices.list.useQuery({
    companyId,
    technicianId,
    ...(range.periodStart ? { periodFrom: range.periodStart } : {}),
    ...(range.periodEnd ? { periodTo: range.periodEnd } : {}),
    limit: 200,
    offset: 0,
  });
  const tiIds = (tiQuery.data?.rows ?? []).map((t) => t.id);
  const paymentsQueries = trpc.useQueries((t) =>
    tiIds.map((id) =>
      t.technicianPayments.list({
        companyId,
        technicianInvoiceId: id,
        limit: 100,
        offset: 0,
      }),
    ),
  );

  const tiByNumber = new Map(
    (tiQuery.data?.rows ?? []).map((t) => [t.id, t.invoiceNumberExternal]),
  );

  type PaymentRow = NonNullable<(typeof paymentsQueries)[number]["data"]>["rows"][number];
  const allPayments: Array<PaymentRow & { invoiceNumber: string | null }> = paymentsQueries.flatMap(
    (q, idx) =>
      (q.data?.rows ?? []).map((p) => ({
        ...p,
        invoiceNumber: tiByNumber.get(tiIds[idx]!) ?? null,
      })),
  );

  // Ordena por paidAt desc
  allPayments.sort((a, b) => {
    const aDate = new Date(a.paidAt).getTime();
    const bDate = new Date(b.paidAt).getTime();
    return bDate - aDate;
  });

  const totalPaid = allPayments.reduce((acc, p) => acc + Number(p.amount), 0);
  const t = useT();

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-500">
          {t("technicianDetail.paymentsToTechnician")}
        </h3>
        <span className="text-xs text-neutral-500">
          {allPayments.length} {t("technicianDetail.total")} ·{" "}
          {formatMoney(totalPaid, { decimals: 2 })} {t("technicianDetail.net")}
        </span>
      </div>

      {tiQuery.isLoading && (
        <p className="text-sm text-neutral-500">{t("technicianDetail.loading")}</p>
      )}
      {allPayments.length === 0 && !tiQuery.isLoading && (
        <p className="text-sm text-neutral-500 bg-neutral-900 border border-neutral-800 rounded-md p-4">
          {t("technicianDetail.emptyPayments")}
        </p>
      )}

      <div className="rounded-lg border border-neutral-800 bg-neutral-900 overflow-hidden">
        {allPayments.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-neutral-950/50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="text-left px-3 py-2 w-28">{t("technicianDetail.colDate")}</th>
                <th className="text-left px-3 py-2 w-36">{t("technicianDetail.colTi")}</th>
                <th className="text-right px-3 py-2 w-32 font-mono">
                  {t("technicianDetail.colAmount")}
                </th>
                <th className="text-left px-3 py-2 w-32">{t("technicianDetail.colMethod")}</th>
                <th className="text-left px-3 py-2">{t("technicianDetail.colReference")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-800">
              {allPayments.map((p) => (
                <tr key={p.id}>
                  <td className="px-3 py-2 text-xs text-neutral-400">
                    {new Date(p.paidAt).toLocaleDateString("pt-BR")}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {p.invoiceNumber ?? `#${p.technicianInvoiceId}`}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {formatMoney(p.amount, { decimals: 2 })}
                    {p.reversesId && (
                      <span className="ml-2 rounded px-1 py-0.5 text-xs bg-amber-500/15 text-amber-300">
                        {t("technicianDetail.reversal")}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-neutral-400">{p.paymentMethod}</td>
                  <td className="px-3 py-2 text-xs text-neutral-500 font-mono">
                    {p.reference ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

// ============================================================================
// TAB: AUDIT
// ============================================================================

function TabAudit({
  companyId,
  technicianId,
  isAdmin,
}: {
  companyId: number;
  technicianId: number;
  isAdmin: boolean;
}) {
  const auditQuery = trpc.audit.listByCompany.useQuery(
    {
      companyId,
      entityKind: "technician",
      entityId: technicianId,
      limit: 100,
      offset: 0,
    },
    { enabled: isAdmin },
  );

  const t = useT();

  if (!isAdmin) {
    return (
      <p className="text-sm text-neutral-500 bg-neutral-900 border border-neutral-800 rounded-md p-4">
        {t("technicianDetail.auditRequiresAdmin")}
      </p>
    );
  }

  const rows = auditQuery.data?.rows ?? [];
  return (
    <section className="space-y-3">
      <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-500">
        {t("technicianDetail.auditLogOfTechnician")}
      </h3>
      {auditQuery.isLoading && (
        <p className="text-sm text-neutral-500">{t("technicianDetail.loading")}</p>
      )}
      {rows.length === 0 && !auditQuery.isLoading && (
        <p className="text-sm text-neutral-500 bg-neutral-900 border border-neutral-800 rounded-md p-4">
          {t("technicianDetail.emptyAudit")}
        </p>
      )}
      <div className="rounded-lg border border-neutral-800 bg-neutral-900 overflow-hidden">
        {rows.length > 0 && (
          <table className="w-full text-xs">
            <thead className="bg-neutral-950/50 text-neutral-500">
              <tr>
                <th className="text-left px-3 py-2 w-40">{t("technicianDetail.colWhen")}</th>
                <th className="text-left px-3 py-2 w-32">{t("technicianDetail.colAction")}</th>
                <th className="text-left px-3 py-2 w-32">{t("technicianDetail.colBy")}</th>
                <th className="text-left px-3 py-2">{t("technicianDetail.colReason")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-800">
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="px-3 py-2 text-neutral-400 font-mono">
                    {new Date(r.occurredAt).toLocaleString("pt-BR")}
                  </td>
                  <td className="px-3 py-2 font-mono">{r.action}</td>
                  <td className="px-3 py-2 text-neutral-400">
                    {r.userName ?? r.userEmail ?? `#${r.userId}`}
                  </td>
                  <td className="px-3 py-2 text-amber-200">{r.reason ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * F-recado-portal (CEO 2026-10-07) — recado do escritório: abre em pop-up no
 * portal do técnico a cada entrada até ele marcar "Ciente".
 */
function TabRecados({ companyId, technicianId }: { companyId: number; technicianId: number }) {
  const t = useT();
  const utils = trpc.useUtils();
  const [msg, setMsg] = useState("");
  // F-recado-trava (CEO 2026-10-28) — trava o portal até o escritório liberar.
  const [travar, setTravar] = useState(false);
  const listQ = trpc.technicians.portalNotices.useQuery({ companyId, technicianId });
  const sendMut = trpc.technicians.sendPortalNotice.useMutation({
    onSuccess: () => {
      setMsg("");
      setTravar(false);
      void utils.technicians.portalNotices.invalidate();
      void utils.technicians.getById.invalidate();
      void utils.technicians.list.invalidate();
    },
  });
  const resolveMut = trpc.technicians.resolvePortalNotice.useMutation({
    onSuccess: () => {
      void utils.technicians.portalNotices.invalidate();
      void utils.technicians.getById.invalidate();
      void utils.technicians.list.invalidate();
    },
  });
  const fmt = (d: string | Date) =>
    new Date(d).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
  return (
    <section className="space-y-4 rounded-lg border border-neutral-800 bg-neutral-900 p-4">
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-400">
          {t("technicianDetail.noticesTitle")}
        </h3>
        <p className="mt-1 text-xs text-neutral-500">{t("technicianDetail.noticesHint")}</p>
      </div>
      {listQ.data && !listQ.data.hasLogin ? (
        <p className="text-sm text-amber-300">{t("technicianDetail.noticesNoLogin")}</p>
      ) : (
        <div className="space-y-2">
          <textarea
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            rows={5}
            maxLength={2000}
            className={inputClass + " w-full"}
            placeholder={t("technicianDetail.noticesPlaceholder")}
            data-testid="notice-text"
          />
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={travar}
              onChange={(e) => setTravar(e.target.checked)}
              className="mt-0.5"
              data-testid="notice-blocking"
            />
            <span>
              🔒 <strong>{t("technicianDetail.noticesBlockLabel")}</strong>
              <span className="block text-xs text-neutral-500">
                {t("technicianDetail.noticesBlockHint")}
              </span>
            </span>
          </label>
          <button
            type="button"
            disabled={msg.trim().length < 5 || sendMut.isPending}
            onClick={() =>
              sendMut.mutate({ companyId, technicianId, message: msg.trim(), blocking: travar })
            }
            className="rounded-md bg-lime px-3 py-1.5 text-sm font-semibold text-on-lime disabled:opacity-40"
            data-testid="notice-send"
          >
            {travar ? "🔒" : "📣"} {sendMut.isPending ? "…" : t("technicianDetail.noticesSendBtn")}
          </button>
          {resolveMut.error && <p className="text-xs text-red-400">{resolveMut.error.message}</p>}
          {sendMut.error && <p className="text-xs text-red-400">{sendMut.error.message}</p>}
        </div>
      )}
      <div className="divide-y divide-neutral-800 rounded-md border border-neutral-800">
        {(listQ.data?.rows ?? []).length === 0 && (
          <p className="px-3 py-3 text-sm text-neutral-500">{t("technicianDetail.noticesEmpty")}</p>
        )}
        {(listQ.data?.rows ?? []).map((r) => (
          <div key={r.id} className="space-y-1 px-3 py-2.5">
            <div className="flex flex-wrap items-center gap-2 text-xs text-neutral-500">
              <span>{fmt(r.createdAt)}</span>
              {r.createdByName && <span>· {r.createdByName}</span>}
              {r.acknowledgedAt ? (
                <span className="badge b-ok">
                  ✓ {t("technicianDetail.noticesReadPre")} {fmt(r.acknowledgedAt)}
                </span>
              ) : (
                <span className="badge b-warn">{t("technicianDetail.noticesUnread")}</span>
              )}
              {r.doneAt && (
                <span className="badge b-ok">
                  ✔ {t("technicianDetail.noticesDonePre")} {fmt(r.doneAt)}
                </span>
              )}
              {r.blocking &&
                (r.resolvedAt ? (
                  <span className="badge b-mut">
                    🔓 {t("technicianDetail.noticesResolvedPre")} {fmt(r.resolvedAt)}
                    {r.resolvedByName ? ` · ${r.resolvedByName}` : ""}
                  </span>
                ) : (
                  <span className="badge b-warn" data-testid="notice-blocked-badge">
                    🔒 {t("technicianDetail.noticesBlockedBadge")}
                  </span>
                ))}
            </div>
            <p className="whitespace-pre-line text-sm">{r.message}</p>
            {r.blocking && !r.resolvedAt && (
              <button
                type="button"
                disabled={resolveMut.isPending}
                onClick={() => resolveMut.mutate({ companyId, technicianId, noticeId: r.id })}
                className="rounded-md border px-3 py-1 text-xs font-semibold"
                style={{ background: "#DCFCE7", borderColor: "#16A34A", color: "#14532D" }}
                data-testid="notice-resolve"
              >
                ✓ {t("technicianDetail.noticesResolveBtn")}
              </button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex gap-2">
      <dt className="text-neutral-500 w-44 text-xs">{label}:</dt>
      <dd className={mono ? "font-mono" : ""}>{value}</dd>
    </div>
  );
}

function KpiBlock({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "amber" | "red" | "positive";
}) {
  const toneClass =
    tone === "amber"
      ? "text-amber-300"
      : tone === "red"
        ? "text-red-300"
        : tone === "positive"
          ? "text-lime"
          : "";
  return (
    <div>
      <div className="text-xs text-neutral-500">{label}</div>
      <div className={`font-mono text-base ${toneClass}`}>{value}</div>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <ErpShell>
      <div className="space-y-4">{children}</div>
    </ErpShell>
  );
}

const inputClass =
  "rounded-md border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm focus:border-lime outline-none disabled:opacity-50";

function worklogMonday(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function worklogShiftWeek(weekStart: string, dir: -1 | 1): string {
  const d = new Date(weekStart + "T00:00:00");
  d.setDate(d.getDate() + dir * 7);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function worklogWeekNum(weekStart: string): number {
  const d = new Date(weekStart + "T00:00:00Z");
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}
/** dd/mm/aa a partir de "aaaa-mm-dd". */
function formatDateFr(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split("-");
  return d && m && y ? `${d}/${m}/${y.slice(2)}` : iso;
}

/**
 * CEO 2026-10-26 — o técnico recebeu ANTES do cliente pagar o carro: cliente
 * ainda não pagou, ou o último recebimento do cliente é posterior à proforma.
 */
function pagoAntesDoCliente(r: {
  allPaid: boolean;
  customerInvoiceStatus: string;
  clientPaidAt: string | null;
  techPaidAt: string | null;
}): boolean {
  if (!r.allPaid || r.techPaidAt == null) return false;
  if (r.customerInvoiceStatus !== "paid") return true;
  return r.clientPaidAt != null && r.techPaidAt < r.clientPaidAt;
}

const WORKLOG_SERVICE_LABEL: Record<string, string> = {
  dsp: "PDR",
  montage_demontage: "Montage",
  peinture: "Peinture",
  pieces: "Pièces",
};

/** F-worklog — semana → clientes atendidos → PDF / enviar por e-mail. */
function WorklogExportCard({
  companyId,
  technicianId,
}: {
  companyId: number;
  technicianId: number;
}) {
  const t = useT();
  const modals = useModals();
  const [weekStart, setWeekStart] = useState(worklogMonday());
  const weekQ = trpc.worklogs.erpWeek.useQuery({ companyId, technicianId, weekStart });
  const abaPdf = useAbaPendente();
  const pdfMut = trpc.worklogs.erpPdf.useMutation({
    onSuccess: (r) => abaPdf.ir(r.url),
    onError: (e) => {
      abaPdf.fechar();
      void modals.alert({ title: "Worklog", message: e.message, tone: "danger" });
    },
  });
  const emailMut = trpc.worklogs.erpEmail.useMutation({
    onSuccess: (r) =>
      void modals.alert({
        title: "Worklog",
        message: `${t("worklog.emailSentPre")} ${r.sentTo}.`,
      }),
    onError: (e) => void modals.alert({ title: "Worklog", message: e.message, tone: "danger" }),
  });
  const clients = weekQ.data?.clients ?? [];
  return (
    <div className="mb-4 rounded-lg border border-lime/20 bg-lime/[0.03] p-3 space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold">🗒 {t("worklog.exportTitle")}</p>
          <p className="text-[11px] text-neutral-500">{t("worklog.exportHint")}</p>
        </div>
        <div className="flex items-center overflow-hidden rounded-md border border-neutral-700 text-sm">
          <button
            type="button"
            className="px-2.5 py-1 hover:bg-neutral-800"
            onClick={() => setWeekStart((w) => worklogShiftWeek(w, -1))}
          >
            −
          </button>
          <span className="border-x border-neutral-700 px-3 py-1 font-semibold">
            {t("worklog.weekPre")} {worklogWeekNum(weekStart)}
          </span>
          <button
            type="button"
            className="px-2.5 py-1 hover:bg-neutral-800"
            onClick={() => setWeekStart((w) => worklogShiftWeek(w, 1))}
          >
            +
          </button>
        </div>
      </div>
      {clients.length === 0 && <p className="text-xs text-neutral-500">{t("worklog.emptyWeek")}</p>}
      {clients.map((c) => (
        <div
          key={c.customerId}
          className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-neutral-800 bg-neutral-900/50 px-2.5 py-1.5 text-sm"
        >
          <span className="min-w-0">
            <span className="font-medium">{c.customerName}</span>
            <span className="text-neutral-500">
              {" "}
              {/* CEO 2026-09-22: o banner conta CARROS. O serviço (PDR,
                  dégarnissage) aparece ao abrir o worklog — somar os dois no
                  cabeçalho fazia o mesmo carro contar duas vezes. */}
              · {new Set(c.rows.map((r) => r.plate ?? r.date)).size}{" "}
              {new Set(c.rows.map((r) => r.plate ?? r.date)).size === 1
                ? t("worklog.carWordSingular")
                : t("worklog.carsWord")}
            </span>
          </span>
          <span className="flex shrink-0 gap-1.5">
            <button
              type="button"
              disabled={pdfMut.isPending}
              onClick={() => {
                abaPdf.abrir();
                pdfMut.mutate({ companyId, technicianId, customerId: c.customerId, weekStart });
              }}
              className="rounded-md border border-neutral-700 px-2.5 py-1 text-xs hover:border-lime disabled:opacity-40"
            >
              📄 {pdfMut.isPending ? t("worklog.generatingBtn") : t("worklog.pdfBtn")}
            </button>
            <button
              type="button"
              disabled={emailMut.isPending}
              onClick={() =>
                emailMut.mutate({ companyId, technicianId, customerId: c.customerId, weekStart })
              }
              className="rounded-md border border-lime/50 px-2.5 py-1 text-xs text-lime hover:bg-lime/10 disabled:opacity-40"
            >
              ✉ {emailMut.isPending ? t("worklog.generatingBtn") : t("worklog.emailBtn")}
            </button>
          </span>
        </div>
      ))}
    </div>
  );
}
